/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import EntityLink from '../../components/common/entity-link';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';
import { unitVillagesService } from '../../services/unit-villages.service';
import { extractErrorMessage } from '../../utils/error-handler';
// import { useAuditNames } from '../../hooks/usePersonnelLookup';
import type { UnitVillage } from '../../types';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

const UnitVillageView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'unit-villages',
    basePath: '/unit-villages',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.UNIT_VILLAGES);
  const canDelete = useCanDelete(JOB_NAMES.UNIT_VILLAGES);

  const [unitVillage, setUnitVillage] = useState<UnitVillage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Set breadcrumb title to show village name instead of UUID
  useBreadcrumbTitle(unitVillage?.villageName);

  // Use cached personnel lookup for audit names
  // const { createdByName, updatedByName } = useAuditNames(unitVillage?.createdBy, unitVillage?.updatedBy);

  /* ------------------------------------------------------------- */
  /* Load UnitVillage - wait for ID decryption                     */
  /* ------------------------------------------------------------- */
  useEffect(() => {
    if (!isReady || !id) {
      return;
    }

    const fetchUnitVillage = async () => {
      try {
        setLoading(true);
        const data = await unitVillagesService.getById(id);
        setUnitVillage(data);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch unit village mapping details'));
      } finally {
        setLoading(false);
      }
    };

    fetchUnitVillage();
  }, [isReady, id]);

  /* ------------------------------------------------------------- */
  /* Edit + Delete Handlers                                        */
  /* ------------------------------------------------------------- */
  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;

    try {
      await unitVillagesService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete unit village mapping'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  /* ------------------------------------------------------------- */
  /* Loading State                                                 */
  /* ------------------------------------------------------------- */
  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  /* ------------------------------------------------------------- */
  /* Error State                                                   */
  /* ------------------------------------------------------------- */
  if (error || !unitVillage) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Unit village mapping not found'} className="mb-4 w-full" />
        <Button
          label="Back to Unit Villages"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  // Dialog footer for delete confirmation
  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button
        label="Cancel"
        severity="secondary"
        outlined
        onClick={handleDeleteCancel}
      />
      <Button
        label="Delete"
        severity="danger"
        onClick={handleDeleteConfirm}
      />
    </div>
  );

  /* ------------------------------------------------------------- */
  /* Main UI                                                       */
  /* ------------------------------------------------------------- */
  return (
    <div className="p-3" data-testid="SCR-UnitVillage-View">
      {/* Header Section */}
      <ScreenHeader icon="pi pi-map-marker" title="Unit Village Details" onBack={() => navigateToList()} />

      {/* Village Information Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          {/* Icon */}
          <div className="w-12 h-12 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-map-marker text-green-600 dark:text-green-400" style={{ fontSize: '1.25rem' }} />
          </div>

          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">
                {unitVillage.villageName || '-'}
              </h2>
              {unitVillage.isActive ? (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Inactive" severity="danger" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <p className="text-xs text-ink-muted">
              {unitVillage.mandalName || unitVillage.mandal?.mandalName || '-'} &bull; {unitVillage.unit?.name || '-'}
            </p>
          </div>
        </div>

        {/* Edit/Delete buttons aligned with profile */}
        {!(unitVillage.isDelete || unitVillage.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Location Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Location Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Village Name</label>
              <p className="text-sm text-ink">{unitVillage.villageName || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Mandal</label>
              <p className="text-sm text-ink">{unitVillage.mandalName || unitVillage.mandal?.mandalName || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">District</label>
              <p className="text-sm text-ink">{unitVillage.districtName || '-'}</p>
            </div>
          </div>
        </div>

        {/* Unit Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Unit Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Police Unit</label>
              <EntityLink
                type="unit"
                id={unitVillage.unit?._id || (unitVillage as any).unitId}
                label={unitVillage.unit?.name}
              />
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Unit Code</label>
              <p className="text-sm text-ink">{unitVillage.unit?.unitCode || '-'}</p>
            </div>
          </div>
        </div>

        {/* Audit Information */}
        <AuditInfoCard
          createdAt={unitVillage.createdAt}
          updatedAt={unitVillage.updatedAt}
          createdBy={unitVillage.createdBy}
          updatedBy={unitVillage.updatedBy}
        />

        {/* IP Information (if available) */}
        {/* {(unitVillage.createdIp || unitVillage.updatedIp) && (
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">IP Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {unitVillage.createdIp && (
                <div>
                  <label className="text-xs text-ink-muted block mb-0.5">Created IP</label>
                  <p className="text-sm text-ink">{unitVillage.createdIp}</p>
                </div>
              )}
              {unitVillage.updatedIp && (
                <div>
                  <label className="text-xs text-ink-muted block mb-0.5">Updated IP</label>
                  <p className="text-sm text-ink">{unitVillage.updatedIp}</p>
                </div>
              )}
            </div>
          </div>
        )} */}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={deleteDialogOpen}
        onHide={handleDeleteCancel}
        header="Confirm Delete"
        footer={deleteDialogFooter}
        style={{ width: '450px' }}
        data-testid="UnitVillageView.Dialog.Delete"
      >
        <p className="text-sm text-ink-muted">
          Are you sure you want to delete this unit village mapping? This action cannot be undone.
        </p>
      </Dialog>
    </div>
  );
};

export default UnitVillageView;
